import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Chip,
  Stack,
  Tabs,
  Tab,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ApartmentIcon from '@mui/icons-material/Apartment';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { PropertySeoItem, SeoSnapshotBackup, ScheduledReleaseItem } from '../../types/seo';
import SeoAuditScoreCard from './SeoAuditScoreCard';
import RefineRegenerateModal from './RefineRegenerateModal';
import SafeSyncQueueModal from './SafeSyncQueueModal';
import { calculateSeoScore } from '../../utils/seoAIEngine';

interface PropertyListingsSeoTabProps {
  properties: PropertySeoItem[];
  backups: SeoSnapshotBackup[];
  onUpdateProperty: (id: string, updated: Partial<PropertySeoItem>) => void;
  onImmediateSync: (snapshot: SeoSnapshotBackup) => void;
  onQueueRelease: (queueItem: ScheduledReleaseItem) => void;
  onRollback: (backup: SeoSnapshotBackup) => void;
}

export default function PropertyListingsSeoTab({
  properties,
  backups,
  onUpdateProperty,
  onImmediateSync,
  onQueueRelease,
  onRollback,
}: PropertyListingsSeoTabProps) {
  const [selectedPropId, setSelectedPropId] = useState<string>(properties[0]?.id || '');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [activeSubTab, setActiveSubTab] = useState<number>(0); // 0: Metadata & Specs, 1: FAQs & Schema, 2: Schema JSON-LD
  const [htmlViewMode, setHtmlViewMode] = useState<'preview' | 'code'>('preview');

  // Modals state
  const [refineModalOpen, setRefineModalOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const filteredProperties = properties.filter((p) => {
    if (projectFilter === 'all') return true;
    return p.projectKey === projectFilter;
  });

  const activeProperty = properties.find((p) => p.id === selectedPropId) || filteredProperties[0] || properties[0];

  if (!activeProperty) {
    return <Alert severity="info">No properties found.</Alert>;
  }

  const scoreData = calculateSeoScore({
    title: activeProperty.seoTitle,
    description: activeProperty.metaDescription,
    hasFaqs: activeProperty.faqs.length >= 2,
    hasSchema: !!activeProperty.schemaJson?.['@context'],
    hasRera: activeProperty.metaDescription.toLowerCase().includes('rera') || !!activeProperty.gujReraNumber,
    hasVastu: activeProperty.metaDescription.toLowerCase().includes('vastu') || !!activeProperty.vastuFacing,
    hasSuratMention: activeProperty.seoTitle.toLowerCase().includes('surat') || activeProperty.metaDescription.toLowerCase().includes('surat'),
    contentWordCount: activeProperty.highlightsHtml.split(' ').length + 100,
  });

  const handleTitleChange = (val: string) => {
    onUpdateProperty(activeProperty.id, { seoTitle: val, syncStatus: 'draft' });
  };

  const handleDescChange = (val: string) => {
    onUpdateProperty(activeProperty.id, { metaDescription: val, syncStatus: 'draft' });
  };

  const handleHighlightsChange = (val: string) => {
    onUpdateProperty(activeProperty.id, { highlightsHtml: val, syncStatus: 'draft' });
  };

  const handleAddFaq = () => {
    const newFaqs = [
      ...activeProperty.faqs,
      { question: 'What are the booking token terms for this unit in Surat?', answer: 'Booking token terms are transparent with a 100% refund guarantee within 15 days if bank loan is not sanctioned.' },
    ];
    onUpdateProperty(activeProperty.id, { faqs: newFaqs, syncStatus: 'draft' });
  };

  const handleRemoveFaq = (index: number) => {
    const newFaqs = activeProperty.faqs.filter((_, idx) => idx !== index);
    onUpdateProperty(activeProperty.id, { faqs: newFaqs, syncStatus: 'draft' });
  };

  const handleFaqChange = (index: number, field: 'question' | 'answer', val: string) => {
    const newFaqs = activeProperty.faqs.map((f, idx) => (idx === index ? { ...f, [field]: val } : f));
    onUpdateProperty(activeProperty.id, { faqs: newFaqs, syncStatus: 'draft' });
  };

  const handleApplyRefinement = (refined: {
    title: string;
    description: string;
    richContent?: string;
    faqs?: { question: string; answer: string }[];
  }) => {
    onUpdateProperty(activeProperty.id, {
      seoTitle: refined.title,
      metaDescription: refined.description,
      ...(refined.richContent ? { highlightsHtml: refined.richContent } : {}),
      ...(refined.faqs ? { faqs: refined.faqs } : {}),
      syncStatus: 'draft',
      lastEditedAt: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) + ' IST',
    });
  };

  const handleCopySchema = () => {
    navigator.clipboard.writeText(JSON.stringify(activeProperty.schemaJson, null, 2));
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2500);
  };

  const getCharMeterColor = (length: number, min: number, max: number) => {
    if (length >= min && length <= max) return '#2e7d32'; // optimal
    if (length > max) return '#d32f2f'; // over limit
    return '#ed6c02'; // too short
  };

  return (
    <Box>
      {/* Top Filter and Actions Bar */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid #e8e8e8', bgcolor: '#fff' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Filter by Project</InputLabel>
              <Select value={projectFilter} label="Filter by Project" onChange={(e) => setProjectFilter(e.target.value)}>
                <MenuItem value="all">All Projects (2 Active)</MenuItem>
                <MenuItem value="swarna-lagom">Swarna Lagom (Ready 2 BHK)</MenuItem>
                <MenuItem value="swarna-avsar">Swarna Avsar (Dec 2026 2 BHK)</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Select Unit / Typology</InputLabel>
              <Select value={activeProperty.id} label="Select Unit / Typology" onChange={(e) => setSelectedPropId(e.target.value)}>
                {filteredProperties.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.projectName} — {p.unitTypology} ({p.possessionStage.includes('Ready') ? 'Ready' : 'Dec 2026'})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => setRefineModalOpen(true)}
              sx={{ color: '#9d6f32', borderColor: '#9d6f32', fontWeight: 700, textTransform: 'none' }}
            >
              ↻ Re-Generate AI
            </Button>
            <Button
              variant="contained"
              startIcon={<ShieldOutlinedIcon />}
              onClick={() => setSyncModalOpen(true)}
              sx={{ bgcolor: '#2e7d32', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#1b5e20' } }}
            >
              Safe Sync & Rollback
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column: Editor & Specifications */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e8e8e8', bgcolor: '#fff', mb: 3 }}>
            {/* Property Badge Strip */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ApartmentIcon sx={{ color: '#9d6f32' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {activeProperty.projectName} — {activeProperty.unitTypology}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={activeProperty.possessionStage}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: activeProperty.possessionStage.includes('Ready') ? 'rgba(46,125,50,0.12)' : 'rgba(237,108,2,0.12)',
                    color: activeProperty.possessionStage.includes('Ready') ? '#2e7d32' : '#ed6c02',
                  }}
                />
                <Chip
                  label={activeProperty.syncStatus.toUpperCase()}
                  size="small"
                  color={activeProperty.syncStatus === 'synced' ? 'success' : 'warning'}
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
            </Stack>

            {/* Quick Specs Summary */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 3, bgcolor: '#fafaf9', borderRadius: 1.5 }}>
              <Grid container spacing={1.5} sx={{ fontSize: 13 }}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Carpet Area:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{activeProperty.carpetAreaSqFt} sq.ft</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Super Built-up:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{activeProperty.superBuiltUpSqFt} sq.ft</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">Vastu Entrance:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{activeProperty.vastuFacing} Facing</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">GujRERA Reg:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 11 }}>{activeProperty.gujReraNumber.slice(0, 16)}...</Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Navigation Sub-Tabs */}
            <Tabs
              value={activeSubTab}
              onChange={(_, v) => setActiveSubTab(v)}
              sx={{ borderBottom: '1px solid #e8e8e8', mb: 2.5 }}
            >
              <Tab label="1. SEO Title & Meta Description" sx={{ fontWeight: 700 }} />
              <Tab label="2. Highlights & Specifications HTML" sx={{ fontWeight: 700 }} />
              <Tab label="3. Buyer FAQs & GujRERA" sx={{ fontWeight: 700 }} />
              <Tab label="4. Schema.org JSON-LD" sx={{ fontWeight: 700 }} />
            </Tabs>

            {/* Tab 0: Meta Title & Description */}
            {activeSubTab === 0 && (
              <Stack spacing={2.5}>
                {/* Title */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#444' }}>
                      SEO Meta Title (Optimal ≤ 60 chars)
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: getCharMeterColor(activeProperty.seoTitle.length, 30, 60),
                      }}
                    >
                      {activeProperty.seoTitle.length}/60 chars
                    </Typography>
                  </Stack>
                  <TextField
                    fullWidth
                    size="small"
                    value={activeProperty.seoTitle}
                    onChange={(e) => handleTitleChange(e.target.value)}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Pattern: <code>[BHK/Type] for Sale in [Project/Locality], Surat | Swarna Group</code>
                  </Typography>
                </Box>

                {/* Description */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#444' }}>
                      Meta Description (Strictly 125 – 155 chars)
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: getCharMeterColor(activeProperty.metaDescription.length, 125, 155),
                      }}
                    >
                      {activeProperty.metaDescription.length} chars (Target: 125-155)
                    </Typography>
                  </Stack>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={activeProperty.metaDescription}
                    onChange={(e) => handleDescChange(e.target.value)}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Must highlight: Carpet Area, Key Amenities, Possession Date, GujRERA Number, and Vastu Facing.
                  </Typography>
                </Box>

                {/* Google SERP Snippet Preview */}
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fbfbfb', borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>
                    Google Search Result Simulator (Mobile & Desktop)
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: '#202124', display: 'block', fontSize: 11 }}>
                      https://www.swarnagroup.in › projects › {activeProperty.projectKey}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#1a0dab',
                        fontWeight: 500,
                        fontSize: 17,
                        lineHeight: 1.3,
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {activeProperty.seoTitle || 'Property Title in Surat | Swarna Group'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4d5156', fontSize: 13, mt: 0.5, lineHeight: 1.4 }}>
                      {activeProperty.metaDescription || 'Add a compelling meta description to boost click-through rates.'}
                    </Typography>
                  </Box>
                </Paper>
              </Stack>
            )}

            {/* Tab 1: Highlights & Specs HTML */}
            {activeSubTab === 1 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#444' }}>
                    Property Specifications & Rich Highlights HTML
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant={htmlViewMode === 'preview' ? 'contained' : 'outlined'}
                      startIcon={<VisibilityIcon />}
                      onClick={() => setHtmlViewMode('preview')}
                      sx={{ fontSize: 11, height: 28 }}
                    >
                      Visual Preview
                    </Button>
                    <Button
                      size="small"
                      variant={htmlViewMode === 'code' ? 'contained' : 'outlined'}
                      startIcon={<CodeIcon />}
                      onClick={() => setHtmlViewMode('code')}
                      sx={{ fontSize: 11, height: 28 }}
                    >
                      HTML Code
                    </Button>
                  </Stack>
                </Stack>

                {htmlViewMode === 'preview' ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: '#fff',
                      '& ul': { pl: 2.5, mt: 1 },
                      '& li': { mb: 0.8, fontSize: 13, color: '#333' },
                      '& h4': { m: 0, mb: 1, color: '#9d6f32', fontSize: 15 },
                    }}
                    dangerouslySetInnerHTML={{ __html: activeProperty.highlightsHtml }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    value={activeProperty.highlightsHtml}
                    onChange={(e) => handleHighlightsChange(e.target.value)}
                    sx={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                )}
              </Box>
            )}

            {/* Tab 2: Buyer FAQs */}
            {activeSubTab === 2 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    RERA, Bank Loan & Possession FAQs ({activeProperty.faqs.length})
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleAddFaq}
                    sx={{ color: '#9d6f32', fontWeight: 700 }}
                  >
                    + Add FAQ
                  </Button>
                </Stack>

                <Stack spacing={2}>
                  {activeProperty.faqs.map((faq, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafaf9' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                        <TextField
                          label={`Question #${idx + 1}`}
                          fullWidth
                          size="small"
                          value={faq.question}
                          onChange={(e) => handleFaqChange(idx, 'question', e.target.value)}
                          sx={{ mr: 1, bgcolor: '#fff' }}
                        />
                        <IconButton size="small" color="error" onClick={() => handleRemoveFaq(idx)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <TextField
                        label="Verified Answer"
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                        value={faq.answer}
                        onChange={(e) => handleFaqChange(idx, 'answer', e.target.value)}
                        sx={{ bgcolor: '#fff' }}
                      />
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Tab 3: Schema.org JSON-LD */}
            {activeSubTab === 3 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#444' }}>
                    Valid Schema.org RealEstateListing & Apartment JSON-LD
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopySchema}
                    sx={{ color: '#9d6f32', fontWeight: 700 }}
                  >
                    {copySuccess ? 'Copied JSON-LD!' : 'Copy Structured Data'}
                  </Button>
                </Stack>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: '#1e1e1e',
                    color: '#4ec9b0',
                    fontFamily: 'monospace',
                    fontSize: 12,
                    borderRadius: 2,
                    maxHeight: 320,
                    overflow: 'auto',
                  }}
                >
                  <pre style={{ margin: 0 }}>
                    {JSON.stringify(activeProperty.schemaJson, null, 2)}
                  </pre>
                </Paper>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right Column: SXO Score & E-E-A-T Check */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={3}>
            <SeoAuditScoreCard score={scoreData.score} checks={scoreData.checks} />

            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e8e8e8', bgcolor: '#fff' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                Sync & Indexing Status
              </Typography>
              <Stack spacing={1.5} sx={{ fontSize: 13 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Current Status:</Typography>
                  <Chip
                    label={activeProperty.syncStatus.toUpperCase()}
                    size="small"
                    color={activeProperty.syncStatus === 'synced' ? 'success' : 'warning'}
                    sx={{ fontWeight: 700, height: 22 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Last Sync:</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{activeProperty.lastSyncedAt || 'Pending push'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Target City:</Typography>
                  <Typography sx={{ fontWeight: 600 }}>Surat, Gujarat</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Target Domain:</Typography>
                  <Typography sx={{ fontWeight: 600, color: '#9d6f32' }}>www.swarnagroup.in</Typography>
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      {/* Refine & Re-Generate Modal */}
      <RefineRegenerateModal
        open={refineModalOpen}
        onClose={() => setRefineModalOpen(false)}
        entityType="PROPERTY"
        currentTitle={activeProperty.seoTitle}
        currentDescription={activeProperty.metaDescription}
        currentRichContent={activeProperty.highlightsHtml}
        currentFaqs={activeProperty.faqs}
        projectName={activeProperty.projectName}
        typology={activeProperty.unitTypology}
        locality={activeProperty.microMarket}
        possessionStage={activeProperty.possessionStage}
        carpetArea={activeProperty.carpetAreaSqFt}
        priceInr={activeProperty.priceInr}
        reraNumber={activeProperty.gujReraNumber}
        onApply={handleApplyRefinement}
      />

      {/* Safe Sync & Queue Modal */}
      <SafeSyncQueueModal
        open={syncModalOpen}
        onClose={() => setSyncModalOpen(false)}
        entityId={activeProperty.id}
        entityType="PROPERTY"
        entityName={`${activeProperty.projectName} (${activeProperty.unitTypology})`}
        currentData={{
          seoTitle: activeProperty.seoTitle,
          metaDescription: activeProperty.metaDescription,
          richContentHtml: activeProperty.highlightsHtml,
          faqs: activeProperty.faqs,
          schemaJson: activeProperty.schemaJson,
        }}
        backups={backups}
        onImmediateSync={(snapshot) => {
          onImmediateSync(snapshot);
          onUpdateProperty(activeProperty.id, {
            syncStatus: 'synced',
            lastSyncedAt: snapshot.timestamp,
          });
        }}
        onQueueRelease={(queueItem) => {
          onQueueRelease(queueItem);
          onUpdateProperty(activeProperty.id, {
            syncStatus: 'staged',
          });
        }}
        onRollback={(backup) => {
          onRollback(backup);
          onUpdateProperty(activeProperty.id, {
            seoTitle: backup.snapshotData.seoTitle,
            metaDescription: backup.snapshotData.metaDescription,
            highlightsHtml: backup.snapshotData.richContentHtml,
            faqs: backup.snapshotData.faqs,
            schemaJson: backup.snapshotData.schemaJson,
            syncStatus: 'synced',
          });
        }}
      />
    </Box>
  );
}
